import { useEffect, useRef, useState } from "react";
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { AntiMetalButton } from '../components/ui/anti-metal-button';

function rand(seed, offset = 0) {
  const x = Math.sin(seed * 9301 + offset * 49297 + 233) * 43758.5453;
  return x - Math.floor(x);
}
const COLS = 4, ROWS = 4, TOTAL = COLS * ROWS;
const IMAGE_SEEDS = ["ai1", "ai2", "ai3", "ai4", "ai5", "ai6", "ai7", "ai8", "ai9", "ai10", "ai11", "ai12", "ai13", "ai14", "ai15", "ai16"];
function shuffleZones() {
  const zones = [];
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) zones.push({ col: c, row: r });
  for (let i = zones.length - 1; i > 0; i--) { const j = Math.floor(rand(i, 7) * (i + 1));[zones[i], zones[j]] = [zones[j], zones[i]]; }
  return zones;
}
const ZONES = shuffleZones();
const PARTICLES = IMAGE_SEEDS.map((seed, i) => {
  const zone = ZONES[i];
  const cx = ((zone.col + 0.5 + (rand(i, 10) - 0.5) * 0.3) / COLS) * 100;
  const cy = ((zone.row + 0.5 + (rand(i, 11) - 0.5) * 0.3) / ROWS) * 100;
  return { src: `https://picsum.photos/seed/${seed}/600/600`, cx: Math.max(2, Math.min(98, cx)), cy: Math.max(2, Math.min(98, cy)), phase: i / TOTAL, maxSizeVw: 16 + rand(i, 4) * 4, baseOpacity: 0.45 + rand(i, 5) * 0.3, depth: 0.4 + rand(i, 12) * 0.6 };
});

const SERVICES = [
  { id: "01", title: "Business Intelligence & Dashboard Development", desc: "Build custom dashboards and reporting systems using tools such as Looker Studio, Tableau, Power BI, and GA4 to monitor business performance in real time." },
  { id: "02", title: "Customer Journey & Funnel Analytics", desc: "Identify bottlenecks across acquisition, conversion, retention, and customer lifecycle stages to improve overall marketing efficiency and profitability." },
  { id: "03", title: "Marketing Analytics & Attribution", desc: "Gain deeper visibility into campaign performance, customer acquisition costs, channel effectiveness, and return on advertising spend." },
  { id: "04", title: "AI Workflow Automation", desc: "Automate repetitive business processes, reporting tasks, lead qualification workflows, customer communication systems, and operational processes using modern AI tools." },
  { id: "05", title: "Data Strategy & Performance Audits", desc: "Evaluate existing data infrastructure, tracking systems, analytics implementation, and reporting processes to uncover growth opportunities and improve decision-making." },
  { id: "06", title: "Predictive Insights & Growth Intelligence", desc: "Leverage data analysis and AI-powered insights to identify trends, forecast opportunities, and support strategic business planning." },
];

function GridBg() {
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 28px,rgba(236,43,40,0.04) 28px,rgba(236,43,40,0.04) 29px)" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(90deg,transparent,transparent 79px,rgba(236,43,40,0.025) 79px,rgba(236,43,40,0.025) 80px)" }} />
      <div style={{ position: "absolute", top: 0, right: 0, width: 320, height: 320, background: "radial-gradient(circle at top right,rgba(236,43,40,0.06) 0%,transparent 65%)" }} />
    </div>
  );
}

// ─── SERVICES — auto-scroll both mobile & desktop ──────────────────────────
function ServicesSection() {
  const [activeIdx, setActiveIdx] = useState(0);
  const itemRefs = useRef([]);
  const autoTimerRef = useRef(null);
  const isAutoRef = useRef(false);

  // Scroll-driven: update activeIdx based on which item is near center
  useEffect(() => {
    const onScroll = () => {
      if (isAutoRef.current) return; // don't fight auto-scroll
      const mid = window.innerHeight * 0.42;
      let closest = 0, closestDist = Infinity;
      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const dist = Math.abs(r.top + r.height / 2 - mid);
        if (dist < closestDist) { closestDist = dist; closest = i; }
      });
      setActiveIdx(closest);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // smooth scroll to item
  const scrollToItem = (i) => {
    const el = itemRefs.current[i];
    if (!el) return;
    const r = el.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;
    // on mobile, center in viewport; on desktop, align to 42% point
    const offset = isMobile ? window.innerHeight * 0.5 - r.height / 2 : window.innerHeight * 0.42 - r.height / 2;
    const target = window.scrollY + r.top - offset;
    isAutoRef.current = true;
    window.scrollTo({ top: target, behavior: "smooth" });
    setTimeout(() => { isAutoRef.current = false; }, 900);
  };

  // Auto-cycle every 2.8s
  useEffect(() => {
    const start = () => {
      autoTimerRef.current = setInterval(() => {
        setActiveIdx(prev => {
          const next = (prev + 1) % SERVICES.length;
          scrollToItem(next);
          return next;
        });
      }, 2800);
    };
    start();
    return () => clearInterval(autoTimerRef.current);
  }, []);

  // Click → jump + reset timer
  const handleClick = (i) => {
    clearInterval(autoTimerRef.current);
    setActiveIdx(i);
    scrollToItem(i);
    autoTimerRef.current = setInterval(() => {
      setActiveIdx(prev => {
        const next = (prev + 1) % SERVICES.length;
        scrollToItem(next);
        return next;
      });
    }, 2800);
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  return (
    <section style={{ background: "#fff", borderTop: "1px solid #ebebeb", fontFamily: "'Bricolage Grotesque',sans-serif" }}>
      <div style={{ padding: "18px clamp(24px,5vw,72px)", borderBottom: "1px solid #ebebeb", fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#bbb" }}>
        What We Do
      </div>

      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row" }}>

        {/* LEFT sticky (desktop only) */}
        {!isMobile && (
          <div style={{ width: "40%", flexShrink: 0, position: "sticky", top: 0, height: "100vh", borderRight: "1px solid #ebebeb", display: "flex", flexDirection: "column", justifyContent: "center", padding: "clamp(48px,6vw,88px)" }}>
            <p style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#bbb", marginBottom: 16 }}>Our Services</p>
            <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "clamp(2.2rem,3.8vw,4.5rem)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.05, color: "#111", marginBottom: 36 }}>
              Data &amp; AI<br />solutions.
            </h2>
            <p style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: 11, letterSpacing: "0.15em", color: "#ec2b28", marginBottom: 14 }}>
              {String(activeIdx + 1).padStart(2, "0")} / {String(SERVICES.length).padStart(2, "0")}
            </p>
            <div style={{ minHeight: 110, marginBottom: 40 }}>
              <p style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "clamp(0.82rem,1vw,0.95rem)", lineHeight: 1.85, color: "#555", fontWeight: 400, transition: "opacity 0.4s" }}>
                {SERVICES[activeIdx].desc}
              </p>
            </div>
            {/* dots */}
            <div style={{ display: "flex", gap: 8, marginBottom: 40 }}>
              {SERVICES.map((_, i) => (
                <button key={i} onClick={() => handleClick(i)} style={{ width: i === activeIdx ? 24 : 8, height: 8, borderRadius: 4, background: i === activeIdx ? "#111" : "#ddd", border: "none", cursor: "pointer", transition: "all 0.3s", padding: 0 }} />
              ))}
            </div>
            <a href="#contact" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 32px", background: "#111", color: "#fff", fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", textDecoration: "none", borderRadius: 40, width: "fit-content", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#ec2b28"}
              onMouseLeave={e => e.currentTarget.style.background = "#111"}>
              Get started →
            </a>
          </div>
        )}

        {/* RIGHT — scrollable items */}
        <div style={{ flex: 1 }}>

          {/* Mobile header */}
          {isMobile && (
            <div style={{ padding: "28px 20px 24px", borderBottom: "1px solid #ebebeb" }}>
              <p style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "#bbb", marginBottom: 10 }}>Our Services</p>
              <h2 style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "clamp(1.8rem,7vw,2.8rem)", fontWeight: 800, letterSpacing: "-0.03em", color: "#111" }}>
                Data &amp; AI solutions.
              </h2>
            </div>
          )}

          {SERVICES.map((s, i) => {
            const isActive = activeIdx === i;
            return (
              <div
                key={s.id}
                ref={el => itemRefs.current[i] = el}
                onClick={() => handleClick(i)}
                style={{
                  borderBottom: "1px solid #ebebeb",
                  padding: isMobile
                    ? "clamp(20px,4vw,32px) 20px"
                    : "clamp(28px,4vw,52px) clamp(40px,6vw,80px)",
                  cursor: "pointer",
                  transition: "opacity 0.35s ease, background 0.3s",
                  opacity: isActive ? 1 : 0.18,
                  background: isActive ? "#fafafa" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 20,
                  minHeight: isMobile ? "clamp(70px,12vw,90px)" : "clamp(100px,13vh,138px)",
                }}
              >
                <div style={{ flex: 1 }}>
                  <span style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: isMobile ? "clamp(1rem,4.5vw,1.3rem)" : "clamp(1.3rem,2.2vw,2.6rem)", fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.2, color: "#111", display: "block" }}>
                    {s.title}
                  </span>
                  {/* On mobile show desc inline under active */}
                  {isMobile && isActive && (
                    <p style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: "0.85rem", lineHeight: 1.75, color: "#555", fontWeight: 400, marginTop: 10 }}>
                      {s.desc}
                    </p>
                  )}
                </div>
                <span style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: 11, color: "#ccc", letterSpacing: "0.14em", flexShrink: 0 }}>{s.id}</span>
              </div>
            );
          })}

          {/* Mobile CTA */}
          {isMobile && (
            <div style={{ padding: "28px 20px" }}>
              <a href="#contact" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 32px", background: "#111", color: "#fff", fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", textDecoration: "none", borderRadius: 40 }}>
                Get started →
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function MergedPage() {
  const ctaRef = useRef(null);
  const particleRefs = useRef([]);
  const rafRef = useRef(null);
  const pausedRef = useRef(null);
  const pausedTRef = useRef({});
  const accumulatedRef = useRef(0);
  const lastNowRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const smoothMouseRef = useRef({ x: 0.5, y: 0.5 });
  const smoothSizeRef = useRef(PARTICLES.map(p => p.maxSizeVw * (p.phase * p.phase)));
  const smoothOpacityRef = useRef(PARTICLES.map(() => 0));
  const cursorRef = useRef(null);

  useEffect(() => {
    lastNowRef.current = performance.now();
    const onMM = e => { mouseRef.current = { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight }; };
    const onTM = e => { if (e.touches.length > 0) mouseRef.current = { x: e.touches[0].clientX / window.innerWidth, y: e.touches[0].clientY / window.innerHeight }; };
    window.addEventListener("mousemove", onMM);
    window.addEventListener("touchmove", onTM, { passive: true });
    const cls = [() => window.removeEventListener("mousemove", onMM), () => window.removeEventListener("touchmove", onTM)];
    PARTICLES.forEach((_, i) => {
      const el = particleRefs.current[i]; if (!el) return;
      const en = () => { pausedRef.current = i; }, le = () => { pausedRef.current = null; };
      el.addEventListener("mouseenter", en); el.addEventListener("mouseleave", le);
      el.style.pointerEvents = "all"; el.style.cursor = "pointer";
      cls.push(() => { el.removeEventListener("mouseenter", en); el.removeEventListener("mouseleave", le); });
    });
    const tick = now => {
      const delta = (now - lastNowRef.current) / 1000; lastNowRef.current = now;
      if (pausedRef.current === null) accumulatedRef.current += delta;
      const elapsed = accumulatedRef.current;
      const isMob = window.innerWidth <= 768;
      const sm = smoothMouseRef.current, tm = mouseRef.current;
      sm.x += (tm.x - sm.x) * (isMob ? 0.1 : 0.05);
      sm.y += (tm.y - sm.y) * (isMob ? 0.1 : 0.05);
      const mx = sm.x - 0.5, my = sm.y - 0.5;
      PARTICLES.forEach((p, i) => {
        const el = particleRefs.current[i]; if (!el) return;
        const isH = pausedRef.current === i, anyH = pausedRef.current !== null;
        let t;
        if (isH) { if (pausedTRef.current[i] === undefined) pausedTRef.current[i] = (elapsed / 5 + p.phase) % 1; t = pausedTRef.current[i]; }
        else { pausedTRef.current[i] = undefined; t = (elapsed / 5 + p.phase) % 1; }
        const tSz = p.maxSizeVw * (t * t) * (isMob ? 1.7 : 1);
        let tOp = p.baseOpacity;
        if (t < 0.12) tOp *= t / 0.12; if (t > 0.78) tOp *= (1 - t) / 0.22;
        if (anyH && !isH) tOp = 0.06; if (isH) tOp = 1;
        smoothSizeRef.current[i] += (tSz - smoothSizeRef.current[i]) * 0.055;
        smoothOpacityRef.current[i] += (tOp - smoothOpacityRef.current[i]) * (isH || anyH ? 0.12 : 0.045);
        el.style.width = `${smoothSizeRef.current[i]}vw`;
        el.style.height = `${smoothSizeRef.current[i]}vw`;
        el.style.opacity = smoothOpacityRef.current[i];
        el.style.zIndex = isH ? 10 : 1; el.style.transition = "none";
        el.style.transform = `translate(calc(-50% + ${mx * 70 * p.depth}px),calc(-50% + ${my * 70 * p.depth}px))`;
      });
      if (ctaRef.current) {
        const s = Math.max(0.75, Math.min(1, 1 - window.scrollY / (window.innerHeight * 1.2)));
        ctaRef.current.style.transform = `scale(${s})`;
        ctaRef.current.style.opacity = Math.max(0, Math.min(1, s * 1.5 - 0.1));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(rafRef.current); cls.forEach(f => f()); };
  }, []);

  useEffect(() => {
    const mv = e => { if (cursorRef.current) { cursorRef.current.style.left = e.clientX + "px"; cursorRef.current.style.top = e.clientY + "px"; } };
    window.addEventListener("mousemove", mv);
    return () => window.removeEventListener("mousemove", mv);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
        :root{--bg:#0a0a0a;--bg2:#111;--red:#ec2b28;--text:#e8e8e8;--muted:#666;--border:rgba(236,43,40,0.14);}
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}

        body{font-family:'Bricolage Grotesque',sans-serif;}

        .ai-cursor{position:fixed;width:14px;height:14px;border:1.5px solid #ec2b28;border-radius:50%;pointer-events:none;z-index:9999;transform:translate(-50%,-50%);mix-blend-mode:screen;}
        @media(max-width:768px){.ai-cursor{display:none;}}

        .wg-sticky{position:sticky;top:0;height:100vh;z-index:1;}
        .wg-stage{position:relative;width:100%;height:100%;overflow:hidden;background:#fff;}
        .wg-particle{position:absolute;overflow:hidden;pointer-events:none;will-change:transform,width,height,opacity;border-radius:3px;transform:translate(-50%,-50%);}
        .wg-particle img{width:100%;height:100%;object-fit:cover;display:block;}
        .wg-center{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;z-index:20;pointer-events:none;}
        .wg-cta{font-family:'Bricolage Grotesque',sans-serif;font-size:clamp(2.4rem,6vw,7rem);font-weight:900;letter-spacing:-0.04em;color:#111;text-decoration:none;pointer-events:all;transform-origin:center;will-change:transform,opacity;mix-blend-mode:multiply;}
        .wg-next{position:relative;z-index:2;background:var(--bg);}

        /* ticker */
        .ticker{overflow:hidden;border-top:1px solid var(--border);border-bottom:1px solid var(--border);padding:13px 0;background:var(--bg2);}
        .ticker-inner{display:flex;gap:56px;width:max-content;animation:tick 28s linear infinite;}
        @keyframes tick{from{transform:translateX(0);}to{transform:translateX(-50%);}}
        .ticker-item{font-family:'Bricolage Grotesque',sans-serif;font-size:10px;letter-spacing:0.2em;text-transform:uppercase;color:var(--muted);white-space:nowrap;display:flex;align-items:center;gap:18px;}
        .ticker-item::before{content:'◆';color:var(--red);font-size:6px;}

        /* sections */
        .sec{padding:clamp(60px,9vw,120px) clamp(24px,6vw,100px);position:relative;}
        .sec-label{font-family:'Bricolage Grotesque',sans-serif;font-size:10px;letter-spacing:0.25em;text-transform:uppercase;color:var(--red);margin-bottom:48px;display:flex;align-items:center;gap:16px;}
        .sec-label::after{content:'';flex:1;height:1px;background:var(--border);}

        /* overview */
        .ov-grid{display:grid;grid-template-columns:1fr 1fr;gap:clamp(40px,5vw,80px);align-items:start;}
        @media(max-width:768px){.ov-grid{grid-template-columns:1fr;gap:32px;}}
        .ov-h{font-family:'Bricolage Grotesque',sans-serif;font-size:clamp(1.7rem,3.2vw,3.2rem);font-weight:800;line-height:1.1;letter-spacing:-0.025em;color:var(--text);}
        .ov-h .red{color:var(--red);}
        .ov-body{font-family:'Bricolage Grotesque',sans-serif;font-size:clamp(0.82rem,1vw,0.95rem);line-height:1.9;color:var(--muted);font-weight:400;}
        .ov-body p+p{margin-top:18px;}

        /* cta */
        .cta-sec{text-align:center;padding:clamp(60px,9vw,120px) clamp(24px,6vw,100px);position:relative;overflow:hidden;}
        .cta-sec::before{content:'';position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:560px;height:560px;background:radial-gradient(circle,rgba(236,43,40,0.07) 0%,transparent 65%);pointer-events:none;}
        .cta-h{font-family:'Bricolage Grotesque',sans-serif;font-size:clamp(1.8rem,4vw,4.5rem);font-weight:900;letter-spacing:-0.03em;line-height:1.1;margin-bottom:18px;color:var(--text);}
        .cta-sub{font-family:'Bricolage Grotesque',sans-serif;font-size:clamp(0.82rem,1vw,0.95rem);color:var(--muted);max-width:460px;margin:0 auto 40px;line-height:1.85;font-weight:400;}
        .ai-btn{font-family:'Bricolage Grotesque',sans-serif;font-size:11px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;padding:15px 36px;background:var(--red);color:#fff;border:none;cursor:pointer;clip-path:polygon(0 0,calc(100% - 10px) 0,100% 10px,100% 100%,10px 100%,0 calc(100% - 10px));transition:opacity .2s,transform .15s;text-decoration:none;display:inline-block;}
        .ai-btn:hover{opacity:.85;transform:translateY(-1px);}
      `}</style>

      <div ref={cursorRef} className="ai-cursor" />

      <Navbar />

      {/* ── HERO ── */}
      <div style={{ position: "relative" }}>
        <div className="wg-sticky">
          <div className="wg-stage">
            {PARTICLES.map((p, i) => (
              <div key={i} ref={el => particleRefs.current[i] = el} className="wg-particle"
                style={{ left: `${p.cx}%`, top: `${p.cy}%`, width: `${p.maxSizeVw * (p.phase * p.phase)}vw`, height: `${p.maxSizeVw * (p.phase * p.phase)}vw`, opacity: 0, zIndex: 1 }}>
                <img src={p.src} alt="" loading="lazy" />
              </div>
            ))}
            <div className="wg-center">
              <a ref={ctaRef} href="#overview" className="wg-cta">Aurelix Labs</a>
            </div>
          </div>
        </div>

        {/* ── DARK SECTIONS ── */}
        <div className="wg-next">

          {/* ticker */}
          <div className="ticker">
            <div className="ticker-inner">
              {["Business Intelligence", "Funnel Analytics", "AI Automation", "Marketing Attribution", "Data Strategy", "Predictive Insights", "Growth Intelligence", "Dashboard Development",
                "Business Intelligence", "Funnel Analytics", "AI Automation", "Marketing Attribution", "Data Strategy", "Predictive Insights", "Growth Intelligence", "Dashboard Development"]
                .map((t, i) => <span key={i} className="ticker-item">{t}</span>)}
            </div>
          </div>

          {/* overview */}
          <section className="sec" id="overview" style={{ background: "var(--bg)" }}>
            <GridBg />
            <p className="sec-label">Overview</p>
            <div className="ov-grid">
              <h2 className="ov-h">Most businesses drown<br />in data. We turn it into<br /><span className="red">your edge.</span></h2>
              <div className="ov-body">
                <p>Modern businesses generate massive amounts of data every day, but most organizations struggle to convert that information into actionable insights.</p>
                <p>We help companies build analytics frameworks, reporting systems, customer intelligence dashboards, and AI-powered workflows that provide clarity, improve decision-making, and unlock growth opportunities.</p>
                <p>Whether you need visibility into marketing performance, customer behavior, operational efficiency, or scalable automation — our team combines analytics expertise with practical AI implementation tailored to your business.</p>
              </div>
            </div>
          </section>

          {/* services */}
          <ServicesSection />

          {/* cta */}
          <section className="cta-sec" style={{ background: "var(--bg)" }}>
            <GridBg />
            <div style={{ position: "relative", zIndex: 1 }}>
              <p style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--red)", marginBottom: 22 }}>Ready to start?</p>
              <h2 className="cta-h">Faster. Smarter.<br /><span style={{ color: "var(--red)" }}>More profitable.</span></h2>
              <p className="cta-sub">Our goal is simple: help businesses make faster, smarter, and more profitable decisions through data, analytics, and intelligent automation.</p>
              <AntiMetalButton as="a" href="/contact">Start the Conversation</AntiMetalButton>
            </div>
          </section>

        </div>
      </div>

      <Footer />
    </>
  );
}